import { usePlayer } from '../../../context/PlayerContext'
import { IconButton } from '../../common'
import { cn } from '../../../utils'
import styles from './PlayHistoryPanel.module.css'

function formatDuration(seconds: number): string {
  if (!seconds) return '--:--'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function formatPlayedAt(isoString: string): string {
  const date = new Date(isoString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`

  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`

  return date.toLocaleDateString()
}

export function PlayHistoryPanel() {
  const {
    playHistory,
    isHistoryOpen,
    closeHistory,
    clearHistory,
    addToQueue,
  } = usePlayer()

  const handlePlayAgain = (track: any) => {
    // Convert PlayHistoryItem back to Release-like object
    const mockRelease = {
      id: track.releaseId,
      title: track.releaseTitle,
      artists: track.artist,
      videos: [{
        src: track.videoUrl,
        uri: track.videoUrl,
        title: track.title,
        duration: track.duration,
      }],
      images: track.thumbnail ? [{
        uri: track.thumbnail,
        uri150: track.thumbnail,
      }] : null,
    } as any

    addToQueue(mockRelease, 0)
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(styles.backdrop, isHistoryOpen && styles.visible)}
        onClick={closeHistory}
      />

      {/* Panel */}
      <div className={cn(styles.panel, isHistoryOpen && styles.open)}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <h2>
              <i className="bi bi-clock-history"></i>
              Play History
            </h2>
            <span className={styles.historyInfo}>
              {playHistory.length} tracks
            </span>
          </div>
          <div className={styles.headerActions}>
            {playHistory.length > 0 && (
              <IconButton variant="ghost" icon="bi bi-trash" aria-label="Clear history" title="Clear history" onClick={clearHistory} />
            )}
            <IconButton variant="ghost" icon="bi bi-x-lg" aria-label="Close history" onClick={closeHistory} />
          </div>
        </div>

        <div className={styles.content}>
          {playHistory.length === 0 ? (
            <div className={styles.emptyState}>
              <i className="bi bi-clock-history"></i>
              <p>No history yet</p>
              <span>Tracks you play will appear here</span>
            </div>
          ) : (
            <div className={styles.trackList}>
              {playHistory.map((track, index) => (
                <div key={`${track.id}-${track.playedAt}-${index}`} className={styles.track}>
                  {track.thumbnail ? (
                    <img src={track.thumbnail} alt="" className={styles.trackThumb} />
                  ) : (
                    <div className={styles.noThumb}>
                      <i className="bi bi-music-note"></i>
                    </div>
                  )}
                  <div className={styles.trackInfo}>
                    <div className={styles.trackTitle}>{track.title}</div>
                    <div className={styles.trackMeta}>
                      {track.artist} · {formatPlayedAt(track.playedAt)}
                    </div>
                  </div>
                  <span className={styles.trackDuration}>
                    {formatDuration(track.duration)}
                  </span>
                  <IconButton variant="ghost" size="sm" icon="bi bi-plus-lg" aria-label="Add to queue" title="Add to queue" onClick={() => handlePlayAgain(track)} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
